import {Formik, Form, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formikControl/FormikControl'
import toast from 'react-hot-toast'
import {createTripNumber, updateTripNumber} from './api'
import {handleApiError} from '../../../helpers/errorHandler'
import {TripNumberBase, TripNumberFormValues, FormActionMap, FormType} from './type'
import {
  mapAPIDataToFormValues,
  mapFormDataToCreatePayload,
  mapFormDataToUpdatePayload,
} from './mapper'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

function TripNumberForm({
  initialValues,
  onAfterSubmit,
}: {
  initialValues: TripNumberBase | undefined
  onAfterSubmit: () => void
}) {
  const formType: FormType = initialValues ? 'update' : 'add'

  const formActionMap: FormActionMap = {
    add: {
      action: createTripNumber,
      mapPayload: mapFormDataToCreatePayload,
    },
    update: {
      action: updateTripNumber,
      mapPayload: mapFormDataToUpdatePayload,
    },
  }

  const submitHandler = (
    values: TripNumberFormValues,
    {setSubmitting: setLoading}: FormikHelpers<TripNumberFormValues>
  ) => {
    const toastId = toast.loading('Submitting')
    setLoading(true)

    const executeAction = (actionPromise: Promise<any>) => {
      actionPromise
        .then(() => {
          toast.success('Success', {id: toastId})
          onAfterSubmit()
        })
        .catch((err) => {
          handleApiError(err, toastId)
        })
        .finally(() => {
          setLoading(false)
        })
    }

    if (formType === 'add') {
      const {action, mapPayload} = formActionMap.add
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    } else {
      const {action, mapPayload} = formActionMap.update
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    }
  }

  const formValues: TripNumberFormValues =
    formType === 'update'
      ? mapAPIDataToFormValues(initialValues!)
      : {tripNumber: '', tripNumberStatus: '0'}

  return (
    <div className="px-5 py-3">
      <Formik
        initialValues={formValues}
        validationSchema={Yup.object({
          tripNumber: Yup.string()
            .max(100, 'The Trip Number cannot exceed 100 characters')
            .required('Trip Number is required'),
          tripNumberStatus: Yup.string().required('Trip Number Status is required'),
        })}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, dirty, isValid}) => (
          <Form>
            <FormikControl
              control="input"
              required
              placeholder="Trip Number"
              label="Trip Number"
              name="tripNumber"
            />
            <FormikControl
              control="check"
              type="radio"
              options={[
                {key: 'Active', value: '1'},
                {key: 'Inactive', value: '0'},
              ]}
              required
              placeholder="Trip Number Status"
              label="Trip Number Status"
              name="tripNumberStatus"
            />
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default TripNumberForm
